export interface Bookmark {
	itemIdentifier: string;
	isSharePointFile: boolean;
}

/**
 * Helper for fetching and managing portal bookmarks for the current user.
 *
 * Call `fetchBookmarks()` once on page load to warm the cache.
 * After that every other method is **synchronous** so it can be used
 * freely inside the synchronous `createResourceCard` render path.
 */
export class BookmarkHelpers {
	private static cache: string[] | null = null;

	/**
	 * Fetches the current user's bookmarks from the API and populates
	 * the in-memory cache.  Returns the GUID array.
	 *
	 * Call this **once** early in the page lifecycle (e.g. on
	 * DOMContentLoaded).  Subsequent calls are a no-op while the
	 * cache is warm.
	 */
	public static async fetchBookmarks(): Promise<string[]> {
		if (BookmarkHelpers.cache !== null) {
			return BookmarkHelpers.cache;
		}

		try {
			const response = await fetch("/api/bookmarks", {
				method: "GET",
				credentials: "same-origin",
				headers: { "Accept": "application/json" }
			});

			if (!response.ok) {
				BookmarkHelpers.cache = [];
				return BookmarkHelpers.cache;
			}

			const data: Bookmark[] = await response.json();
			BookmarkHelpers.cache = data.map(b => b.itemIdentifier.toLowerCase());
			return BookmarkHelpers.cache;
		} catch (e) {
			BookmarkHelpers.cache = [];
			return BookmarkHelpers.cache;
		}
	}

	/**
	 * Synchronous check against the in-memory cache.
	 * Returns `false` when the cache has not been loaded yet.
	 */
	public static isBookmarked(identifier: string): boolean {		
		if (!identifier || !BookmarkHelpers.cache) return false;
		return BookmarkHelpers.cache.includes(identifier.toLowerCase());
	}

	/**
	 * Adds an identifier to the local cache (does NOT call the API).
	 */
	public static addToCache(identifier: string): void {
		if (!identifier) return;
		const id = identifier.toLowerCase();
		if (BookmarkHelpers.cache && !BookmarkHelpers.cache.includes(id)) {
			BookmarkHelpers.cache.push(id);
		}
		BookmarkHelpers.notifyUpdated();
	}

	/**
	 * Removes an identifier from the local cache (does NOT call the API).
	 */
	public static removeFromCache(identifier: string): void {
		if (!identifier || !BookmarkHelpers.cache) return;
		const id = identifier.toLowerCase();
		BookmarkHelpers.cache = BookmarkHelpers.cache.filter(i => i !== id);
		BookmarkHelpers.notifyUpdated();
	}

	/**
	 * Current bookmark count from the in-memory cache (0 when not yet loaded).
	 */
	public static getCount(): number {
		return BookmarkHelpers.cache?.length ?? 0;
	}

	/**
	 * Notifies any listeners (e.g. the nav bookmark count bubble) that the bookmark
	 * cache changed, so they can re-render without making their own API call.
	 */
	private static notifyUpdated(): void {
		document.dispatchEvent(new CustomEvent('bookmarks:updated'));
	}

	/**
	 * Synchronously updates the visual state of a `.bookmark-trigger`
	 * element.
	 *
	 * When called without an explicit `bookmarked` value it reads the
	 * element's `data-identifier` and checks the cache.
	 * Pass an explicit boolean to skip the lookup (e.g. right after a
	 * toggle).
	 */
	public static updateBookmarkUI(el: HTMLElement, bookmarked?: boolean): void {
		const isBookmarked = bookmarked !== undefined
			? bookmarked
			: BookmarkHelpers.isBookmarked(el.dataset.identifier);

		const label = el.querySelector('.label');
		if (isBookmarked) {
			el.classList.add('active');
			if (label) label.innerHTML = "Bookmarked";
			el.setAttribute('title', 'Remove Bookmark');
		} else {
			el.classList.remove('active');
			if (label) label.innerHTML = "Bookmark";
			el.setAttribute('title', 'Add Bookmark');
		}
	}
}

